import React, { useEffect, useMemo, useState } from 'react';
import { adminService, paymentsService, usersService } from '../services/api';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import type { User } from '../types';
import { getImageUrl } from '../utils/imageUtils';

type WalletRequestRow = {
  id: string;
  fullName: string;
  phone: string;
  status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CLOSED';
  userId?: string | null;
  amount: number;
  depositMethod: 'CARD' | 'SHEBA' | 'ACCOUNT';
  receiptReference?: string | null;
  receiptImageUrl?: string | null;
  note?: string | null;
  createdAt: string;
  updatedAt: string;
};

const WalletRequests: React.FC = () => {
  const [rows, setRows] = useState<WalletRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CLOSED'>('ALL');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  const [manualTargetUser, setManualTargetUser] = useState<User | null>(null);
  const [manualLookingUp, setManualLookingUp] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualMessage, setManualMessage] = useState('');
  const [selectedRow, setSelectedRow] = useState<WalletRequestRow | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await paymentsService.getAdminWalletTopupRequests({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        search: search.trim() || undefined,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'خطا در دریافت درخواست‌های کیف پول');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const stats = useMemo(() => {
    return {
      total: rows.length,
      pending: rows.filter((r) => r.status === 'NEW' || r.status === 'CONTACTED').length,
      approved: rows.filter((r) => r.status === 'QUALIFIED').length,
      rejected: rows.filter((r) => r.status === 'CLOSED').length,
    };
  }, [rows]);

  const approve = async (row: WalletRequestRow) => {
    setSavingId(row.id);
    try {
      await paymentsService.approveWalletTopupRequest(row.id, { amount: Number(row.amount || 0) });
      await fetchData();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'تایید درخواست ناموفق بود');
    } finally {
      setSavingId(null);
    }
  };

  const reject = async (row: WalletRequestRow) => {
    const reason = window.prompt('دلیل رد درخواست را وارد کنید (اختیاری):') || '';
    setSavingId(row.id);
    try {
      await paymentsService.rejectWalletTopupRequest(row.id, { reason });
      await fetchData();
    } catch (err: any) {
      alert(err?.response?.data?.message || 'رد درخواست ناموفق بود');
    } finally {
      setSavingId(null);
    }
  };

  const lookupManualUser = async () => {
    const query = manualPhone.trim();
    if (!query) return;
    setManualLookingUp(true);
    setManualMessage('');
    setError('');
    try {
      const res = await usersService.getAll({ page: 1, limit: 20, search: query, role: 'USER' });
      const q = query.toLowerCase();
      const normalize = (v?: string | null) => (v || '').toLowerCase().trim();
      const scoreUser = (u: User) => {
        let score = 0;
        const phone = normalize(u.phone);
        const email = normalize(u.email);
        const username = normalize(u.username);
        const firstName = normalize(u.firstName);
        const lastName = normalize(u.lastName);
        const fullName = `${firstName} ${lastName}`.trim();

        if (phone === q) score += 120;
        else if (phone.includes(q)) score += 70;
        if (email === q) score += 100;
        else if (email.includes(q)) score += 65;
        if (username === q) score += 95;
        else if (username.includes(q)) score += 60;
        if (fullName === q) score += 90;
        else if (fullName.includes(q)) score += 55;
        if (firstName === q || lastName === q) score += 45;
        else if (firstName.includes(q) || lastName.includes(q)) score += 30;

        return score;
      };

      const best = [...(res.data || [])]
        .map((u) => ({ u, score: scoreUser(u) }))
        .sort((a, b) => b.score - a.score)[0]?.u || null;

      setManualTargetUser(best);
      if (!best) {
        setError('کاربری با این مشخصات پیدا نشد');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'خطا در جستجوی کاربر');
      setManualTargetUser(null);
    } finally {
      setManualLookingUp(false);
    }
  };

  const submitManualCredit = async () => {
    if (!manualTargetUser?.id) {
      setError('ابتدا کاربر را انتخاب کنید');
      return;
    }
    const amount = Math.round(Number(manualAmount || 0));
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('مبلغ شارژ باید بیشتر از صفر باشد');
      return;
    }
    setManualSubmitting(true);
    setManualMessage('');
    setError('');
    try {
      const res = await adminService.creditUserWallet(manualTargetUser.id, {
        amount,
        description: manualDescription.trim() || undefined,
      });
      setManualMessage(`شارژ انجام شد | موجودی جدید: ${Number(res.walletBalance || 0).toLocaleString('fa-IR')} تومان`);
      setManualAmount('');
      setManualDescription('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'شارژ دستی کیف پول ناموفق بود');
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleDownloadReceipt = async (receiptUrl: string, requestId: string) => {
    try {
      const res = await fetch(receiptUrl);
      if (!res.ok) {
        throw new Error('download_failed');
      }
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const ext = blob.type.includes('png') ? 'png' : blob.type.includes('webp') ? 'webp' : 'jpg';
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `wallet-receipt-${requestId}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      setError('دانلود رسید انجام نشد. دوباره تلاش کنید.');
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
        <h1 className="text-xl font-black text-gray-900 sm:text-2xl">کیف پول و درخواست افزایش موجودی</h1>
        <p className="mt-1.5 text-sm font-medium text-gray-600">
          تمام درخواست‌های افزایش موجودی را بررسی، تایید مالی یا رد کنید. پس از تایید، کیف پول کاربر خودکار شارژ می‌شود.
        </p>
      </div>

      <div className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm sm:p-5">
        <h2 className="text-base font-black text-indigo-900">شارژ مستقیم کیف پول (بدون درخواست)</h2>
        <p className="mt-1 text-xs font-medium text-slate-500">
          کاربر را با موبایل، نام، ایمیل یا نام کاربری پیدا کنید و سپس مبلغ را مستقیم شارژ کنید.
        </p>
        <div className="mt-3 grid gap-3 sm:grid-cols-12">
          <input
            value={manualPhone}
            onChange={(e) => setManualPhone(e.target.value)}
            placeholder="موبایل / نام / ایمیل / نام کاربری"
            className="sm:col-span-4 rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          />
          <button
            type="button"
            onClick={lookupManualUser}
            disabled={manualLookingUp}
            className="sm:col-span-2 rounded-xl bg-indigo-600 px-3 py-2.5 text-sm font-black text-white disabled:opacity-60"
          >
            {manualLookingUp ? 'در حال جستجو...' : 'جستجو'}
          </button>
          <input
            type="number"
            min={1}
            value={manualAmount}
            onChange={(e) => setManualAmount(e.target.value)}
            placeholder="مبلغ شارژ (تومان)"
            className="sm:col-span-3 rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          />
          <button
            type="button"
            onClick={submitManualCredit}
            disabled={manualSubmitting}
            className="sm:col-span-3 rounded-xl bg-emerald-600 px-3 py-2.5 text-sm font-black text-white disabled:opacity-60"
          >
            {manualSubmitting ? 'در حال شارژ...' : 'شارژ مستقیم کیف پول'}
          </button>
          <input
            value={manualDescription}
            onChange={(e) => setManualDescription(e.target.value)}
            placeholder="توضیح عملیات (اختیاری)"
            className="sm:col-span-12 rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          />
        </div>
        {manualTargetUser ? (
          <div className="mt-3 rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
            کاربر انتخاب شده: <span className="font-black">{`${manualTargetUser.firstName || ''} ${manualTargetUser.lastName || ''}`.trim() || manualTargetUser.username}</span>
            <span className="mx-2">|</span>
            {manualTargetUser.phone || 'بدون شماره'}
          </div>
        ) : null}
        {manualMessage ? (
          <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700">
            {manualMessage}
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4"><p className="text-xs text-blue-700">کل</p><p className="text-2xl font-black text-blue-900">{stats.total.toLocaleString('fa-IR')}</p></div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4"><p className="text-xs text-amber-700">در انتظار</p><p className="text-2xl font-black text-amber-900">{stats.pending.toLocaleString('fa-IR')}</p></div>
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4"><p className="text-xs text-emerald-700">تایید شده</p><p className="text-2xl font-black text-emerald-900">{stats.approved.toLocaleString('fa-IR')}</p></div>
        <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4"><p className="text-xs text-rose-700">رد شده</p><p className="text-2xl font-black text-rose-900">{stats.rejected.toLocaleString('fa-IR')}</p></div>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو: نام، موبایل، شماره پیگیری..."
            className="min-w-[16rem] flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          >
            <option value="ALL">همه وضعیت‌ها</option>
            <option value="NEW">جدید</option>
            <option value="CONTACTED">در حال بررسی</option>
            <option value="QUALIFIED">تایید شده</option>
            <option value="CLOSED">رد شده</option>
          </select>
          <button onClick={fetchData} className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-black text-white">اعمال فیلتر</button>
        </div>
      </div>

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div> : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead className="bg-slate-50">
              <tr className="text-right text-[11px] font-black uppercase text-slate-600">
                <th className="px-3 py-3">کاربر</th>
                <th className="px-3 py-3">مبلغ</th>
                <th className="px-3 py-3">روش واریز</th>
                <th className="px-3 py-3">رسید</th>
                <th className="px-3 py-3">وضعیت</th>
                <th className="px-3 py-3">تاریخ</th>
                <th className="px-3 py-3">عملیات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center"><BrandSpinnerRing size="sm" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-500">درخواستی یافت نشد</td></tr>
              ) : rows.map((row) => (
                (() => {
                  const receiptUrl = getImageUrl(row.receiptImageUrl || undefined);
                  return (
                <tr key={row.id} className="border-t border-slate-100 text-sm">
                  <td className="px-3 py-3">
                    <p className="font-bold text-slate-900">{row.fullName || 'کاربر'}</p>
                    <p className="text-xs text-slate-500">{row.phone}</p>
                  </td>
                  <td className="px-3 py-3 font-black text-[#1C3B9B]">{Number(row.amount || 0).toLocaleString('fa-IR')} تومان</td>
                  <td className="px-3 py-3">{row.depositMethod === 'SHEBA' ? 'شبا' : row.depositMethod === 'ACCOUNT' ? 'حساب' : 'کارت'}</td>
                  <td className="px-3 py-3">
                    {receiptUrl ? (
                      <div className="flex items-center gap-2">
                        <a href={receiptUrl} target="_blank" rel="noreferrer" className="text-indigo-700 font-semibold hover:underline">مشاهده رسید</a>
                        <a
                          href={receiptUrl}
                          onClick={(e) => {
                            e.preventDefault();
                            void handleDownloadReceipt(receiptUrl, row.id);
                          }}
                          className="rounded-md border border-slate-200 px-2 py-0.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                        >
                          دانلود
                        </a>
                      </div>
                    ) : row.receiptReference ? (
                      <span className="text-xs text-slate-600">{row.receiptReference}</span>
                    ) : (
                      <span className="text-xs text-slate-400">ندارد</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                      row.status === 'QUALIFIED' ? 'bg-emerald-100 text-emerald-700' : row.status === 'CLOSED' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {row.status === 'QUALIFIED' ? 'تایید مالی' : row.status === 'CLOSED' ? 'رد شده' : 'در انتظار'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-500">{new Date(row.createdAt).toLocaleDateString('fa-IR')}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setSelectedRow(row)}
                        className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-black text-slate-700 hover:bg-slate-50"
                      >
                        مشاهده جزئیات
                      </button>
                      <button
                        onClick={() => approve(row)}
                        disabled={savingId === row.id || row.status === 'QUALIFIED'}
                        className="rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-black text-white disabled:opacity-50"
                      >
                        تایید و شارژ
                      </button>
                      <button
                        onClick={() => reject(row)}
                        disabled={savingId === row.id || row.status === 'CLOSED'}
                        className="rounded-lg bg-rose-600 px-2.5 py-1.5 text-xs font-black text-white disabled:opacity-50"
                      >
                        رد درخواست
                      </button>
                    </div>
                  </td>
                </tr>
                  );
                })()
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedRow ? (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/55 p-4" onClick={() => setSelectedRow(null)}>
          <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-2xl sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-black text-slate-900">جزئیات کامل درخواست</h3>
              <button onClick={() => setSelectedRow(null)} className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-bold text-slate-600">بستن</button>
            </div>
            <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-xs text-slate-500">نام کاربر</p><p className="font-black text-slate-900">{selectedRow.fullName || 'کاربر'}</p></div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-xs text-slate-500">موبایل</p><p className="font-black text-slate-900">{selectedRow.phone}</p></div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-xs text-slate-500">مبلغ</p><p className="font-black text-[#1C3B9B]">{Number(selectedRow.amount || 0).toLocaleString('fa-IR')} تومان</p></div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-xs text-slate-500">روش واریز</p><p className="font-black text-slate-900">{selectedRow.depositMethod === 'SHEBA' ? 'شبا' : selectedRow.depositMethod === 'ACCOUNT' ? 'حساب' : 'کارت'}</p></div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-xs text-slate-500">وضعیت</p><p className="font-black text-slate-900">{selectedRow.status === 'QUALIFIED' ? 'تایید مالی' : selectedRow.status === 'CLOSED' ? 'رد شده' : selectedRow.status === 'CONTACTED' ? 'در حال بررسی' : 'جدید'}</p></div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-xs text-slate-500">شماره پیگیری</p><p className="font-black text-slate-900">{selectedRow.receiptReference || 'ثبت نشده'}</p></div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-xs text-slate-500">تاریخ ثبت</p><p className="font-black text-slate-900">{new Date(selectedRow.createdAt).toLocaleString('fa-IR')}</p></div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-xs text-slate-500">آخرین بروزرسانی</p><p className="font-black text-slate-900">{new Date(selectedRow.updatedAt).toLocaleString('fa-IR')}</p></div>
            </div>
            <div className="mt-3 rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <p className="text-xs text-slate-500">توضیحات کاربر</p>
              <p className="mt-1 font-semibold text-slate-800">{selectedRow.note || 'توضیحی ثبت نشده'}</p>
            </div>
            {getImageUrl(selectedRow.receiptImageUrl || undefined) ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <a
                  href={getImageUrl(selectedRow.receiptImageUrl || undefined) || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-xl bg-indigo-600 px-4 py-2 text-sm font-black text-white hover:bg-indigo-700"
                >
                  مشاهده رسید مشتری
                </a>
                <a
                  href={getImageUrl(selectedRow.receiptImageUrl || undefined) || '#'}
                  onClick={(e) => {
                    e.preventDefault();
                    const url = getImageUrl(selectedRow.receiptImageUrl || undefined);
                    if (!url) return;
                    void handleDownloadReceipt(url, selectedRow.id);
                  }}
                  className="inline-flex rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-50"
                >
                  دانلود رسید
                </a>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default WalletRequests;
